import axios from 'axios'
import { useAuthStore } from '../store/authStore'

// Use local Pages Functions API instead of external API
// In production, this will be the same domain
// In development with wrangler pages dev, it will be localhost:8788 (default)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

console.log('🔧 API Configuration:', {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  API_BASE_URL,
  willUseCurrentOrigin: !API_BASE_URL
})

export const api = axios.create({
  baseURL: API_BASE_URL,
  // Don't set default Content-Type here
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token
    
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {} as any
    }
    
    // Add auth token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Set Content-Type based on data type
    if (config.data instanceof FormData) {
      // For FormData, don't set Content-Type - browser will add boundary
      delete config.headers['Content-Type']
    } else if (!config.headers['Content-Type']) {
      // For everything else, default to JSON
      config.headers['Content-Type'] = 'application/json'
    }
    
    // Log request details for debugging
    console.log('🟢 API Request interceptor:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!token,
      hasAuthHeader: !!config.headers?.['Authorization'],
      isFormData: config.data instanceof FormData,
      contentType: config.headers?.['Content-Type'] || 'NOT SET (browser will set)',
    })
    
    return config
  },
  (error) => {
    console.error('🔴 Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect to login if we're already on the login page or during login attempt
    const isLoginRequest = error.config?.url?.includes('/admin/auth/login')
    const isCurrentlyOnLogin = window.location.pathname === '/login'
    const isFileUpload = error.config?.url?.includes('/files/upload')
    const isLocationRequest = error.config?.url?.includes('/locations')

    // Log 401 errors for debugging
    if (error.response?.status === 401) {
      console.error('🔴 401 Unauthorized error:', {
        url: error.config?.url,
        method: error.config?.method,
        isLoginRequest,
        isCurrentlyOnLogin,
        isFileUpload,
        isLocationRequest,
        hasAuthHeader: !!error.config?.headers?.['Authorization'],
        authHeaderPreview: error.config?.headers?.['Authorization']?.substring(0, 30),
        contentType: error.config?.headers?.['Content-Type'],
        requestBody: error.config?.data,
        response: error.response?.data,
      })
    }

    // TEMPORARILY: Don't logout on file upload or location errors for debugging
    if (error.response?.status === 401 && !isLoginRequest && !isCurrentlyOnLogin && !isFileUpload && !isLocationRequest) {
      console.warn('Session expired or invalid token, redirecting to login...')
      useAuthStore.getState().logout()
      // Small delay to allow any pending requests to complete
      setTimeout(() => {
        window.location.href = '/login'
      }, 100)
    }
    return Promise.reject(error)
  }
)

// API methods
export const adminApi = {
  // Auth
  login: (username: string, password: string) => {
    const payload = { email: username, password }
    console.log('Login request:', {
      url: `${API_BASE_URL}/api/auth/login`,
      payload: { email: username, password: password.substring(0, 3) + '...', passwordLength: password.length }
    })
    return api.post('/api/auth/login', payload)
  },

  verifyTwoFactor: (code: string, tempToken: string) =>
    api.post('/api/auth/verify-2fa', { code }, {
      headers: { Authorization: `Bearer ${tempToken}` }
    }),

  getMe: () => api.get('/api/auth/me'),

  // Admins
  getAdmins: () => api.get('/api/admins'),
  createAdmin: (data: any) => api.post('/api/admins', data),
  updateAdmin: (id: string, data: any) => api.put(`/api/admins/${id}`, data),
  deleteAdmin: (id: string) => api.delete(`/api/admins/${id}`),

  // Users - now directly from D1 via Pages Functions
  getUsers: (params?: any) => api.get('/api/users', { params }),
  getUser: (id: string) => api.get(`/api/users/${id}`),
  updateUser: (id: string, data: any) => api.put(`/api/users/${id}`, data),
  banUser: (id: string, reason: string) => api.post(`/api/users/${id}/ban`, { reason }),
  unbanUser: (id: string) => api.post(`/api/users/${id}/unban`),

  // Trips - now directly from D1 via Pages Functions
  getTrips: (params?: any) => api.get('/api/trips', { params }),
  getTrip: (id: string) => api.get(`/api/trips/${id}`),
  createTrip: (data: any, userId: string) => {
    const finalPayload = { ...data, user_id: userId }
    console.log('🚀 createTrip called:', { 
      data, 
      userId, 
      finalPayload,
      payloadKeys: Object.keys(finalPayload),
      payloadJSON: JSON.stringify(finalPayload, null, 2)
    })
    return api.post('/api/trips', finalPayload)
  },
  updateTrip: (id: string, data: any) => api.put(`/api/trips/${id}`, data),
  deleteTrip: (id: string) => api.delete(`/api/trips/${id}`),

  // Trip Events
  getTripEvents: (tripId: string) => api.get('/api/events', { params: { trip_id: tripId } }),
  createTripEvent: (tripId: string, data: any) => {
    console.log('📤 Creating event:', JSON.stringify({ ...data, trip_id: tripId }, null, 2))
    return api.post('/api/events', { ...data, trip_id: tripId })
  },
  updateTripEvent: (_tripId: string, eventId: string, data: any) => {
    console.log('📤 Updating event:', eventId, JSON.stringify(data, null, 2))
    return api.patch(`/api/events/${eventId}`, data)
  },
  deleteTripEvent: (_tripId: string, eventId: string) => api.delete(`/api/events/${eventId}`),

  // Trip Files
  getTripFiles: (tripId: string, params?: any) => api.get('/api/files', { params: { trip_id: tripId, ...params } }),
  createTripFile: (tripId: string, data: any) => api.post('/api/files', { ...data, trip_id: tripId }),
  updateFile: (fileId: string, data: any) => api.patch(`/api/files/${fileId}`, data),
  deleteTripFile: (_tripId: string, fileId: string) => api.delete(`/api/files/${fileId}`),

  // Trip Notes
  getTripNotes: (tripId: string) => api.get('/api/notes', { params: { trip_id: tripId } }),
  createTripNote: (tripId: string, data: any) => api.post('/api/notes', { ...data, trip_id: tripId }),
  updateTripNote: (_tripId: string, noteId: string, data: any) => api.patch(`/api/notes/${noteId}`, data),
  updateTripNoteChecklistItem: (_tripId: string, noteId: string, data: any) =>
    api.patch(`/api/notes/${noteId}/checklist-item`, data),
  deleteTripNote: (_tripId: string, noteId: string) => api.delete(`/api/notes/${noteId}`),

  // Trip Participants
  getTripParticipants: (tripId: string) => api.get(`/trips/${tripId}/participants`),
  inviteTripParticipant: (tripId: string, data: any) => api.post(`/trips/${tripId}/participants`, data),
  updateTripParticipantRole: (tripId: string, participantId: string, data: any) => api.patch(`/trips/${tripId}/participants/${participantId}`, data),
  removeTripParticipant: (tripId: string, participantId: string) => api.delete(`/trips/${tripId}/participants/${participantId}`),

  // Trip Locations
  getTripLocations: (tripId: string) => api.get(`/api/trips/${tripId}/locations`),
  addTripLocation: (tripId: string, data: { name: string; latitude: number; longitude: number; address?: string }) => {
    console.log('📍 addTripLocation called with:', { tripId, data })
    // API expects location data to be nested under "location" key
    const payload = { location: data }
    console.log('📍 Sending payload:', payload)
    return api.post(`/api/trips/${tripId}/locations`, payload)
  },
  removeTripLocation: (tripId: string, locationId: string) => api.delete(`/api/trips/${tripId}/locations/${locationId}`),

  // Events
  getEvents: (params?: any) => api.get('/api/events', { params }),
  createEvent: (data: any, userId: string) => api.post('/api/events', { ...data, userId }),
  updateEvent: (id: string, data: any) => {
    // Map camelCase to snake_case for backend
    const payload: any = {}
    if ('title' in data) payload.title = data.title
    if ('description' in data) payload.description = data.description
    if ('startDate' in data) payload.start_date = data.startDate
    if ('endDate' in data) payload.end_date = data.endDate
    if ('status' in data) payload.status = data.status
    if ('visibility' in data) payload.privacy = data.visibility // visibility maps to privacy in backend
    if ('coverImage' in data) payload.cover_image = data.coverImage
    if ('coverImageFileId' in data) payload.cover_image_file_id = data.coverImageFileId
    if ('tags' in data) payload.tags = data.tags
    if ('location' in data) payload.location = data.location
    return api.patch(`/api/events/${id}`, payload)
  },
  deleteEvent: (id: string) => api.delete(`/api/events/${id}`),

  // Content - Comments
  getComments: (params?: any) => api.get('/admin/content/comments', { params }),
  deleteComment: (id: string) => api.delete(`/admin/content/comments/${id}`),

  // Posts
  getPosts: (params?: any) => api.get('/api/posts', { params }),
  getPost: (id: string) => api.get(`/api/posts/${id}`),
  createPost: (data: any) => api.post('/api/posts', data),
  updatePost: (id: string, data: any) => api.patch(`/api/posts/${id}`, data),
  deletePost: (id: string) => api.delete(`/api/posts/${id}`),
  blockPost: (id: string) => api.patch(`/api/posts/${id}`, { isBlocked: true }),
  unblockPost: (id: string) => api.patch(`/api/posts/${id}`, { isBlocked: false }),

  // File upload
  uploadFile: (file: File, data?: { 
    trip_id?: string
    event_id?: string
    user_id?: string  // Waybook user to link the file to (not the uploader)
    privacy?: 'public' | 'trip_members' | 'event_members' | 'private' | 'custom'
    description?: string
    tags?: string[]
  }) => {
    const formData = new FormData()
    formData.append('file', file)
    if (data?.trip_id) formData.append('trip_id', data.trip_id)
    if (data?.event_id) formData.append('event_id', data.event_id)
    if (data?.user_id) formData.append('user_id', data.user_id)  // Links file to waybook user
    if (data?.privacy) formData.append('privacy', data.privacy)
    if (data?.description) formData.append('description', data.description)
    if (data?.tags?.length) {
      formData.append('tags', JSON.stringify(data.tags))
    }
    
    // Get token explicitly
    const token = useAuthStore.getState().token
    console.log('🔵 Uploading file:', { 
      hasToken: !!token, 
      tokenLength: token?.length || 0,
      tokenPreview: token ? token.substring(0, 30) + '...' : 'NO TOKEN',
      tripId: data?.trip_id,
      eventId: data?.event_id,
      userId: data?.user_id,  // Waybook user to link file to
      privacy: data?.privacy,
      fileName: file.name,
      fileSize: file.size,
    })
    
    // Log FormData contents for debugging
    console.log('📦 FormData contents:')
    for (const [key, value] of formData.entries()) {
      console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value)
    }
    
    // Use native fetch instead of axios for file upload to avoid Content-Type issues
    return fetch(`${API_BASE_URL}/api/files/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type - browser will add boundary automatically
      },
      body: formData,
    }).then(async (response) => {
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }
      return { data }
    })
  },

  // Chat messages
  getChatMessages: (params?: any) => api.get('/admin/chats/messages', { params }),
  deleteChatMessage: (chatId: string, messageId: string) => api.delete(`/admin/chats/${chatId}/messages/${messageId}`),

  // System accounts (Waybook accounts)
  getWaybookAccounts: () => api.get('/api/system-accounts'),
  createWaybookAccount: (data: any) => api.post('/api/system-accounts', data),

  // Search
  search: (query: string, type?: string) => api.get('/api/dashboard/search', { params: { q: query, type } }),

  // Audit log
  getAuditLog: (params?: any) => api.get('/api/audit-log', { params }),

  // Stats
  getStats: () => api.get('/api/dashboard/overview'),

  // Moderation queue
  getModerationQueue: (params?: any) => api.get('/api/moderation/queue', { params }),

  // Recommended content (public endpoint from main API)
  getRecommendedContent: (params?: {
    limit?: number
    offset?: number
    contentType?: 'all' | 'trip' | 'post' | 'event'
    latitude?: number
    longitude?: number
    radius?: number
  }) => axios.get('https://api.waybook.me/recommended', { params }),
  
  // Moderation actions
  approveComment: (id: string) => api.post(`/admin/content/comments/${id}/approve`),
  rejectComment: (id: string) => api.post(`/admin/content/comments/${id}/reject`),
  
  approveTrip: (id: string) => api.post(`/admin/trips/${id}/approve`),
  rejectTrip: (id: string, reason: string) => api.post(`/admin/trips/${id}/reject`, { reason }),
  
  approveEvent: (id: string) => api.post(`/admin/events/${id}/approve`),
  rejectEvent: (id: string, reason: string) => api.post(`/admin/events/${id}/reject`, { reason }),
  
  approvePhoto: (id: string) => api.post(`/admin/content/photos/${id}/approve`),
  rejectPhoto: (id: string) => api.post(`/admin/content/photos/${id}/reject`),
}

export default api
